import { useAuth } from '@hourly/contexts/AuthContext';
import {
  Button,
  ButtonVariant,
  type Column,
  SearchInput,
  Table,
  Tooltip,
} from '@hourly-hds/components';
import { Question } from '@hourly-hds/icons/glyph';
import { useState } from 'react';
import AsyncDisplay from '@shared/components/AsyncDisplay';
import useDebounceCallback from '@shared/data/hooks/useDebounceCallback';
import Page from '../../../_layout/components/Page';
import ReferralStatusChip from '../../_shared/components/ReferralStatusChip';
import NewReferralDialog from '../components/NewReferralDialog';
import { useActiveReferrals } from './data/hooks/useActiveReferrals';
import useCreateReferral from './data/hooks/useCreateReferral';
import { Referral, ReferralStatus } from './data/models/referrals.model';
import styles from './Referrals.view.module.css';

const columns: Column<Referral>[] = [
  {
    header: 'company name',
    accessorKey: 'name',
  },
  {
    header: 'start date',
    accessorKey: 'startDate',
    align: 'right',
  },
  {
    header: 'status',
    accessorKey: 'status',
    cell: (info: { getValue: () => ReferralStatus }) => (
      <ReferralStatusChip status={info.getValue()} />
    ),
  },
  {
    header: () => (
      <>
        aging
        <Tooltip text="Aging represent days in current status">
          <Question />
        </Tooltip>
      </>
    ),
    accessorKey: 'aging',
  },
] as Column<Referral>[];

export default function ReferralIndex() {
  const { sessionState } = useAuth();
  const { referralsPage, fetchReferralsPage, isSettled } = useActiveReferrals(
    sessionState.partnerId ?? ''
  );
  const createNewReferral = useCreateReferral();
  const [search, setSearch] = useState('');

  const [isReferralFormOpen, setIsReferralFormOpen] = useState(false);

  const toggleDisplayReferralForm = () => {
    setIsReferralFormOpen((open) => !open);
  };

  const handlePageChange = (page: number) => {
    fetchReferralsPage({ page, search });
  };

  const debouncedSearch = useDebounceCallback((value: string) => {
    fetchReferralsPage({ page: 1, search: value });
  }, 300);

  return (
    <Page>
      <Page.Header>
        <Page.Title>Active Referrals</Page.Title>
        <Button
          label="New Referral"
          variant={ButtonVariant.Primary}
          onClick={toggleDisplayReferralForm}
        />
      </Page.Header>

      <Page.Content>
        <div className={styles.search}>
          <SearchInput
            aria-label={'search referrals'}
            onChange={(value) => {
              setSearch(value);
              debouncedSearch(value);
            }}
            value={search}
            placeholder="Search"
          />
        </div>
        <AsyncDisplay isSettled={isSettled} hasData={referralsPage.totalItems > 0}>
          <div className={styles.tableContainer}>
            <Table
              data={referralsPage.items}
              columns={columns}
              totalRecords={referralsPage.totalItems}
              currentPage={referralsPage.current}
              onPageChange={handlePageChange}
            />
          </div>
        </AsyncDisplay>
      </Page.Content>

      <NewReferralDialog
        isOpen={isReferralFormOpen}
        onClose={toggleDisplayReferralForm}
        onFinish={async (newReferral) => {
          await createNewReferral(newReferral);
          setSearch('');
          fetchReferralsPage({ page: 1, search: '' });
          toggleDisplayReferralForm();
        }}
      />
    </Page>
  );
}
